/**
 * The menu bar, organised by what you are examining rather than by what kind
 * of operation it is.
 *
 * The old bar was File / Preprocess / Registration / Segmentation / Perfusion
 * / Quantification — categories of *operation*. That is how the code is
 * organised, not how the work is. Someone reading a diffusion study does not
 * think "I need a registration"; they think "I am doing the DWI read, and
 * registration is a step in it". Splitting by operation meant the DWI workflow
 * had no home, and its steps were scattered across four menus that each also
 * held things belonging to perfusion.
 *
 * So the top level is the study you are working on — Diffusion Weighted
 * Imaging, Perfusion — plus the things that cut across both.
 *
 * **One rule, applied to all seven:** clicking the name opens that page;
 * clicking the chevron opens the menu. No entry behaves differently from its
 * neighbours, which is the property the previous bar lacked.
 *
 * Leaves point at ids in `tools/catalog.ts` or `usecases/catalog.ts`. Nothing
 * is written twice: `tests/nav-config.test.mjs` fails the build if an id here
 * does not exist, and if any OpenADS capability is unreachable from this bar.
 */

import type { TabId } from "@/shell/tabRegistry";

export interface NavLeaf {
  /** A tool from `tools/catalog.ts`. Opens with its parameters in the panel. */
  toolId?: string;
  /** A use case from `usecases/catalog.ts`. */
  useCaseId?: string;
  /** A raw action id on the action bus, for shell commands. */
  action?: string;
  /** Overrides the tool's own label where the context makes a better name. */
  label?: string;
  /** Third level: the leaf appears under `group › submenu`. */
  submenu?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  /** Shown under the group heading when it needs explaining. */
  hint?: string;
  items: NavLeaf[];
}

export interface NavSection {
  id: string;
  label: string;
  /** The unabbreviated name, where there is room for it. */
  full?: string;
  /** For the status bar and narrow windows. */
  short: string;
  /** Where clicking the name goes. Absent means the section is menu-only. */
  tab?: TabId;
  blurb: string;
  groups: NavGroup[];
}

const tool = (toolId: string, extra: Omit<NavLeaf, "toolId"> = {}): NavLeaf => ({ toolId, ...extra });
const useCase = (useCaseId: string, extra: Omit<NavLeaf, "useCaseId"> = {}): NavLeaf => ({
  useCaseId,
  ...extra,
});

/**
 * What actually runs in the browser, and nothing else.
 *
 * `tools/catalog.ts` has around ninety entries, because it was written for the
 * desktop app, where every one of them calls a real Docker/SimpleITK backend.
 * In this browser build, exactly two computations have a real in-browser
 * implementation today (see `wasm-engine/engine.ts`'s own "Scope of this
 * pass" note and `tools/stageRun/StageRunPanel.tsx`'s `isWiredToday`): brain
 * extraction's two stages, `gen_mask` (`prep.brainMask`, "Brain Mask") and
 * `skull_strip` (`prep.skullStrip`, "Skull Strip"). A third catalog entry,
 * `prep.applyMask`, points at the same `skull_strip` stage as `prep.skullStrip`
 * — its own description says "Same as Skull Strip" — so it is left out here
 * rather than shown as a second button that does the first button's job.
 * `seg.brain` is the same duplication of `prep.brainMask` under a different
 * id, left out for the same reason.
 *
 * Previously this menu was generated from the full catalog — every tab,
 * every `group`/`subgroup`, as nested flyouts — which meant showing about
 * twenty-five categories where roughly twenty-three of them could only ever
 * open a popup that says "not available in this browser build yet". That is
 * not a toolbox, it is a wall of disabled buttons with two live ones
 * somewhere inside it, and it was genuinely unusable: nested flyouts inside a
 * scrolling dropdown container render outside that container's visible
 * scrolled area (an absolutely-positioned descendant still counts toward its
 * nearest scrolling ancestor's overflow), so on top of the clutter, most of
 * the flyouts could not even be seen.
 *
 * So: a flat list of what is real. When a catalog entry's stage gets a real
 * in-browser implementation, add it here as one more flat row — not by
 * regenerating the whole catalog into the menu again.
 */
function toolsSection(): NavSection {
  const groups: NavGroup[] = [
    {
      id: "tools.brainExtraction",
      label: "Brain Extraction",
      items: [tool("prep.brainMask"), tool("prep.skullStrip")],
    },
  ];

  // ML/DL used to be its own bar entry, and before that held six placeholder
  // training/evaluation rows that were all `unavailable` — a whole slot for
  // disabled rows was heavier than the capability deserved, and none of it
  // ran. Replaced with real, plug-and-play third-party models: official ONNX
  // exports we don't convert ourselves, fetched (and cached) only once the
  // reader explicitly asks, never bundled or downloaded up front. Kept to a
  // couple of classic examples rather than a model zoo — see `ml.sam`/
  // `ml.medsam` in `tools/catalog.ts` for what each one is and, for MedSAM,
  // why it is not wired yet.
  // No `submenu` on either leaf: both render as flat rows directly under
  // "ML / DL" with their real name visible (status is a badge on the row,
  // drawn by `MenuBar`'s `LeafRow`, not a `Flyout` label standing in for the
  // name — that combination used to hide "Segment Anything (SAM)" and
  // "MedSAM" a level deeper, behind flyouts literally labelled "Ready" and
  // "Not yet available").
  groups.push({
    id: "tools.learning",
    label: "ML / DL",
    hint: "Third-party segmentation models, not OpenADS's own — each downloads only when you run it.",
    items: [tool("ml.sam"), tool("ml.medsam")],
  });

  // Real, officially-published itk-wasm pipelines — see the `itk.*` entries
  // in `tools/catalog.ts` for what each one is and why it's real (not a
  // stub) despite being added after the "flat list of what is real" note
  // above. No `submenu` on any of them, same reasoning as ML/DL: four flat
  // rows, not a fifth nested flyout.
  groups.push({
    id: "tools.imageProcessing",
    label: "Image Processing",
    hint: "Official itk-wasm pipelines — fetched only when you run them.",
    items: [tool("itk.downsample"), tool("itk.compareImages"), tool("itk.morphInterp"), tool("itk.dicomImport")],
  });

  // The same real DWI registration (ANTs Affine+SyN) and lesion segmentation
  // (DAGMNet DWI ONNX) the main pipeline runs, offered standalone — pick the
  // input volume(s) yourself rather than running the whole protocol. See
  // `dwi.register`/`dwi.segmentStroke` in `tools/catalog.ts` and
  // `lib/dwi-pipeline.ts`, which both this menu and `wasm-engine/engine.ts`
  // call, so the two paths cannot drift apart.
  groups.push({
    id: "tools.dwiStages",
    label: "DWI Pipeline Stages",
    hint: "The same real ANTs/DAGMNet computation the DWI pipeline runs, on whichever volumes you pick.",
    items: [tool("dwi.register"), tool("dwi.segmentStroke")],
  });

  return {
    id: "tools",
    label: "Tools",
    short: "Tools",
    // No `tab`: the name opens the menu, the same as Help — Tools has no page
    // of its own, every leaf opens its own popup (`ToolLaunchDialog`) instead.
    blurb:
      "Every operation the software can apply to an image, filed by what it does rather than by which pipeline happens to use it.",
    groups,
  };
}

export const NAV: NavSection[] = [
  // ------------------------------------------------------------- 1. Home ---
  {
    id: "home",
    label: "Home",
    short: "Home",
    tab: "home",
    blurb: "Workspaces, recent studies and what you can do.",
    groups: [
      {
        id: "home.workspace",
        label: "Workspace",
        items: [
          { action: "workspace.new", label: "New workspace…" },
          { action: "workspace.open", label: "Open workspace…" },
          { action: "workspace.save", label: "Save workspace" },
        ],
      },
      {
        id: "home.study",
        label: "Study",
        items: [
          useCase("data.openSubject", { label: "Open subject folder…" }),
          useCase("data.reviewDataset", { label: "Scan a folder of subjects…" }),
          useCase("data.importDicom", { label: "Import from DICOM…" }),
          useCase("data.openDataset", { label: "Open dataset of subjects…" }),
          useCase("data.reopen", { label: "Recent studies" }),
        ],
      },
      {
        id: "home.app",
        label: "Application",
        items: [
          { action: "file.preferences", label: "Preferences…" },
          { action: "case.close", label: "Close study" },
        ],
      },
    ],
  },

  // ------------------------------------ 2. Diffusion Weighted Imaging ---
  {
    id: "dwi",
    label: "DWI",
    short: "DWI",
    /** Spelled out in the menu header and the tooltip, not in the bar. */
    full: "Diffusion Weighted Imaging",
    tab: "dwi",
    blurb: "The OpenADS DWI pipeline: from raw diffusion data to the stroke core and its report.",
    groups: [
      {
        id: "dwi.run",
        label: "Run",
        items: [
          useCase("read.dwi", { label: "Run the whole DWI analysis" }),
          useCase("read.segmentOnly", { label: "Re-run segmentation only" }),
          useCase("read.reportOnly", { label: "Re-run reporting only" }),
        ],
      },
      {
        id: "dwi.preprocess",
        label: "Preprocess",
        hint: "The pipeline's own steps. Every other image operation is under Tools.",
        items: [
          tool("prep.prepdata", { label: "Prepare raw data" }),
          tool("prep.brainMask", { label: "Generate brain mask" }),
          tool("prep.skullStrip", { label: "Skull strip" }),
          tool("prep.applyMask", { label: "Apply brain mask" }),
        ],
      },
      {
        id: "dwi.registration",
        label: "Registration",
        items: [
          tool("reg.dwiMni", { label: "DWI → MNI152" }),
          tool("reg.adcMni", { label: "ADC → MNI152" }),
          tool("reg.affinesyn", { label: "Affine + SyN" }),
          tool("reg.quick", { label: "Quick register (affine)" }),
        ],
      },
      {
        id: "dwi.segmentation",
        label: "Segmentation",
        items: [
          tool("seg.stroke", { label: "Stroke core — DAGMNet" }),
          tool("seg.brain", { label: "Brain mask — SynthStrip" }),
        ],
      },
      {
        id: "dwi.reporting",
        label: "Reporting",
        items: [
          tool("q.report", { label: "Generate report and QFV" }),
          useCase("read.review", { label: "Open results" }),
          useCase("data.export", { label: "Export results…" }),
        ],
      },
      {
        id: "dwi.qc",
        label: "Quality control",
        items: [
          tool("reg.overlay", { label: "Registration overlay" }),
          tool("reg.similarityQc", { label: "Image similarity" }),
          tool("reg.difference", { label: "Difference image" }),
          tool("reg.checkerboard", { submenu: "Not wrapped yet" }),
          tool("reg.jacobian", { submenu: "Not wrapped yet" }),
          { action: "dock.show.qc", label: "QC checklist" },
        ],
      },
    ],
  },

  // ------------------------------------------------------- 3. Perfusion ---
  {
    id: "perfusion",
    label: "Perfusion",
    short: "PWI",
    tab: "perfusion",
    blurb: "The OpenADS PWI pipeline: from the 4D series to hypoperfusion and mismatch.",
    groups: [
      {
        id: "pwi.run",
        label: "Run",
        items: [
          useCase("read.pwi", { label: "Run the whole perfusion analysis" }),
          useCase("read.mismatch", { label: "Run DWI + perfusion together" }),
        ],
      },
      {
        id: "pwi.prepare",
        label: "Prepare",
        items: [
          tool("pwi.validate", { label: "Validate the 4D series" }),
          tool("pwi.prepare", { label: "Motion and slice-time correction" }),
        ],
      },
      {
        id: "pwi.maps",
        label: "Maps",
        items: [
          tool("pwi.ttp", { label: "Generate TTP" }),
          tool("pwi.tmax", { label: "Tmax / CBF / CBV" }),
        ],
      },
      {
        id: "pwi.registration",
        label: "Registration",
        items: [
          tool("reg.ttpDwi", { label: "TTP → DWI" }),
          tool("pwi.coreg", { label: "TTP–ADC coregistration" }),
        ],
      },
      {
        id: "pwi.segmentation",
        label: "Segmentation",
        items: [
          tool("seg.hp", { label: "Hypoperfusion — DAGMNet" }),
          tool("pwi.hp", { label: "Hypoperfusion (pipeline stage)" }),
        ],
      },
      {
        id: "pwi.mismatch",
        label: "Mismatch",
        items: [
          tool("pwi.mismatch", { label: "Core against hypoperfusion" }),
          tool("q.hpVolume", { label: "Hypoperfusion volume" }),
        ],
      },
      {
        id: "pwi.reporting",
        label: "Reporting",
        items: [
          tool("q.pwiReport", { label: "Generate the perfusion report" }),
          useCase("read.review", { label: "Open results" }),
        ],
      },
    ],
  },

  // ---------------------------------------------------------- 4. Output ---
  // The most important page for a deployed reader — every run ends here — so
  // it gets a name in the bar rather than living a click below Home the way
  // it used to. It has no menu of its own: there is one place to browse a
  // run's files and one viewer to read them in, not a list of settings.
  {
    id: "output",
    label: "Output",
    short: "Output",
    tab: "output",
    blurb: "Every project and output folder your runs have produced, browsable down to the file.",
    groups: [],
  },

  // ---------------------------------------------------------- 5. Tools ---
  // Folds in what used to be two more bar entries: Visualization (how the
  // shared viewer is controlled now lives on the viewer's own toolbar, next
  // to the pixels, rather than in a menu above them) and ML/DL (see
  // `toolsSection`, which appends it as a group).
  toolsSection(),

  // ------------------------------------------------------------- 6. Help ---
  {
    id: "help",
    label: "Help",
    short: "Help",
    blurb: "",
    groups: [
      {
        id: "help.app",
        label: "ADS Desk",
        items: [
          { action: "shell.shortcuts", label: "Keyboard shortcuts" },
          { action: "shell.about", label: "About ADS Desk" },
          { action: "shell.commandPalette", label: "Search commands…" },
        ],
      },
    ],
  },
];

export const NAV_BY_ID = new Map(NAV.map((s) => [s.id, s]));

/** Leaves of one group, bucketed by their `submenu` (undefined = top level). */
export function groupLeaves(group: NavGroup): { submenu?: string; items: NavLeaf[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, NavLeaf[]>();
  for (const leaf of group.items) {
    const key = leaf.submenu ?? "";
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(leaf);
  }
  return order.map((key) => ({ submenu: key || undefined, items: buckets.get(key)! }));
}
